import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { OrderService } from '../../../services/order.service';
import { Order } from '../../../model/interfaces/order';
import { Visitor } from '../../../model/visitor';
import { Share } from '../../../model/interfaces/share';
import { TicketPackageInstance } from '../../../model/interfaces/ticket-package-instance';
import { VisitorService, PlayerService, OffCanvasService } from 'src/app/services';
import { Fee } from 'src/app/model/interfaces/fee';
import { PaymentModel } from 'src/app/model/enums/payment-model';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { CheckoutState } from 'src/app/model/enums/checkout-state';
import { InfoPricingUpdateComponent } from 'src/app/components/info-pricing-update.component/info-pricing-update.component';
import { PricingUpdateComponent } from '../pricing-update.component/pricing-update.component';
import { OrderDisplayData, OrderLine } from 'src/app/model/interfaces/order-display-data';
import { OrderDisplayDataService } from 'src/app/services/order-display-data.service';
import { PaymentInterval } from '../../../model/types/payment-interval';

@Component({
  selector: 'app-basket',
  templateUrl: './basket.component.html',
  styleUrls: ['./basket.component.scss'],
})
export class BasketComponent implements OnInit, OnDestroy {
  public order: Order;
  public topPrize: number;
  public winChance: number;
  public priceTotal: number;
  public visitor: Visitor;
  public packageOrderLines;
  public shareOrderLines;
  public itemCount: number;
  public pendingDeleteItem: any;
  public fees: Fee[] = [];
  public paymentClass: string;
  public locked = false;
  public pricePrMonth: number;
  public playerLoggedIn = false;
  public pricingUpdateExpanded = false;
  public priceChangeInEffect = false;

  @Input() public checkoutState: CheckoutState;

  @Input('navigate-checkout')
  public navigateCheckout: boolean;

  @Input('payment-details')
  public paymentDetailsVisible: boolean;

  @Input('payment-model')
  public paymentModel: PaymentModel = PaymentModel.Subscription;

  @Input()
  public active: boolean;

  @Input()
  public theme: string;

  @Input('price-factor')
  public classCount: PaymentInterval = 6;

  @Output() public onCloseClick = new EventEmitter();

  @ViewChild('pricingUpdate')
  public pricingUpdate: PricingUpdateComponent;

  // Represents the first day of the five draw dates in the 1st possible class for an internet internet sale.
  public drawDateLast: Date;

  // Represents the last date of the five draw dates in the last class for an internet sale.
  public drawDateFirst: Date;
  public orderLines: OrderLine[];
  public disabled: boolean;
  public isBsAppActive: boolean;
  public orderTotalPrice: number;
  public fmtTotalPrice: number;

  private readonly orderService: OrderService;
  private readonly visitorService: VisitorService;
  private readonly playerService: PlayerService;
  private readonly offCanvasService: OffCanvasService;
  private readonly orderDisplayDataService: OrderDisplayDataService;

  private readonly router: Router;
  private onOrderDisplayDataUpdatedSubscription = new Subscription();
  private onOrderExpiredSubscription = new Subscription();
  private onOrderExpirationNoticeSubscription = new Subscription();
  private playerSubscription = new Subscription();
  private onBsAppActiveSubscription = new Subscription();
  private priceSubscription = new Subscription();

  public constructor(
    orderService: OrderService,
    visitorService: VisitorService,
    playerService: PlayerService,
    router: Router,
    offCanvasService: OffCanvasService,
    orderDisplayDataService: OrderDisplayDataService
  ) {
    this.orderService = orderService;
    this.visitorService = visitorService;
    this.playerService = playerService;
    this.router = router;
    this.offCanvasService = offCanvasService;
    this.orderDisplayDataService = orderDisplayDataService;
  }

  public ngOnInit(): void {
    this.orderDisplayDataService.setProperties(
      this.paymentModel,
      this.isBsAppActive,
      this.checkoutState
    );
    // this.update();
    this.getOrderDisplayData();

    this.setupSubscriptions();
    this.setPaymentClass();
    this.setLockedState();
    this.setVisitor();
    this.getPlayerIsLoggedIn();
    console.log('basket init');
  }

  private async getOrderDisplayData() {
    const displayData = await this.orderDisplayDataService.getOrderDisplayData();
    this.updateDisplayData(displayData);
  }

  public deleteOrderItemShare(orderItem: Share): void {
    this.orderService.deleteShareFromOrder(orderItem).then(() => {});
    this.pendingDeleteItem = orderItem;
  }

  public deleteOrderItemPackage(orderItem: TicketPackageInstance): void {
    this.orderService.deletePackageFromOrder(orderItem).then(() => {});
    this.pendingDeleteItem = orderItem;
  }

  private updateDisplayData(orderDisplayData: OrderDisplayData): void {
    this.topPrize = orderDisplayData.topPrize;
    this.winChance = orderDisplayData.winChance;
    this.priceTotal = orderDisplayData.priceTotal;
    this.order = orderDisplayData.order;
    this.orderLines = orderDisplayData.orderLines;
    this.packageOrderLines = orderDisplayData.packageOrderLines;
    this.shareOrderLines = orderDisplayData.shareOrderLines;
    this.itemCount = orderDisplayData.itemCount;
    this.fees = orderDisplayData.fees;
    this.paymentClass = orderDisplayData.paymentClass;
    this.pricePrMonth = orderDisplayData.pricePrMonth;
    this.priceChangeInEffect = orderDisplayData.priceChangeInEffect;
  }

  public close(): void {
    this.onCloseClick.emit();
  }

  private setPaymentClass(): void {
    this.paymentClass = `payment-model-${PaymentModel[this.paymentModel].toLowerCase()}`;
  }

  private setLockedState(): void {
    this.locked = this.paymentModel === PaymentModel.OneTime;
  }

  private setVisitor(): void {
    this.visitor = this.visitorService.getVisitor();
  }

  public stringToNumber(str: string): number {
    return Number(str);
  }

  private setupSubscriptions(): void {
    this.onOrderDisplayDataUpdatedSubscription =
      this.orderDisplayDataService.onOrderDisplayDataUpdated.subscribe(
        (orderDisplayData: OrderDisplayData) => {
          this.updateDisplayData(orderDisplayData);
        }
      );

    this.onOrderExpiredSubscription = this.orderService.onOrderExpired.subscribe(() =>
      this.terminate()
    );
    this.onOrderExpirationNoticeSubscription = this.orderService.onOrderExpirationNotice.subscribe(
      () => this.warnTermination()
    );
  }

  private terminate(): void {
    this.order = null;
    this.close();
  }

  private warnTermination(): void {
    console.log('terminates in a short bit');
  }

  public animateButtonMobile(): void {
    if (this.disabled) {
      return;
    }
    this.disabled = true;
  }

  public getPlayerIsLoggedIn(): void {
    this.playerSubscription = this.playerService
      .getAuthorizedObservable()
      .subscribe((isAutherizered) => {
        if (isAutherizered === true) {
          this.playerLoggedIn = true;
        } else {
          this.playerLoggedIn = false;
        }
      });
  }

  public onPaymentClicked(): void {
    this.router.navigateByUrl('/min-side/mine-lodsedler?action=openshop');
  }

  public onPricingDetailsClick(): void {
    this.pricingUpdate.expand();
  }

  public onShowPriceUpdateInfoClick(): void {
    this.offCanvasService.show(InfoPricingUpdateComponent, null);
  }

  public onPricingExpand(): void {
    this.pricingUpdateExpanded = true;
  }

  public ngOnDestroy(): void {
    this.onOrderDisplayDataUpdatedSubscription.unsubscribe();
    this.onOrderExpiredSubscription.unsubscribe();
    this.onOrderExpirationNoticeSubscription.unsubscribe();
    this.playerSubscription.unsubscribe();
    this.onBsAppActiveSubscription.unsubscribe();
    this.priceSubscription.unsubscribe();
  }
}
